import { useCallback, useEffect, useMemo, useState } from 'react';
import { GalleryItem } from './gallery.types';
import { detectType, isBlobUrl, makeId, validateMaxSize } from './gallery.utils';
import { ClientGalleryService } from '../service';

export function useClientGallery(clientId: string) {
    const [items, setItems] = useState<GalleryItem[]>([]);
    const [loadingList, setLoadingList] = useState(false);
    const [uploading, setUploading] = useState(false);

    const [preview, setPreview] = useState<GalleryItem | null>(null);
    const [openCamera, setOpenCamera] = useState(false);

    const galleryService = useMemo(() => new ClientGalleryService(), []);

    const total = items.length;

    const fetchGallery = useCallback(async () => {
        if (!clientId) return;

        setLoadingList(true);
        try {
            const { data } = await galleryService.list(clientId);

            setItems(
                (data || []).map((it: any) => ({
                    id: it.id, // id do vínculo (clients_gallery)
                    type: it.type,
                    url: it.url, // url do storage (signed)
                    name: it.name,
                    size: it.size,
                    createdAt: it.createdAt,
                    status: 'ready',
                }))
            );
        } finally {
            setLoadingList(false);
        }
    }, [clientId, galleryService]);

    // carrega ao trocar cliente
    useEffect(() => {
        fetchGallery();
    }, [fetchGallery]);

    // cleanup de blob URLs no unmount
    useEffect(() => {
        return () => {
            items.forEach((it) => {
                if (isBlobUrl(it.url)) URL.revokeObjectURL(it.url);
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const canShowEmpty = !loadingList && items.length === 0;

    const addFiles = useCallback(
        async (files: File[]) => {
            if (!files.length) return;

            // valida tamanho
            const invalid = files.find((f) => !validateMaxSize(f));
            if (invalid) {
                // você pode trocar por snackbar/toast
                alert(`Arquivo muito grande: ${invalid.name}. Limite: 50MB.`);
                return;
            }

            const now = new Date().toISOString();

            // previews temporários
            const tempItems: GalleryItem[] = files.map((file) => ({
                id: makeId(),
                type: detectType(file),
                url: URL.createObjectURL(file),
                name: file.name,
                size: file.size,
                createdAt: now,
                status: 'uploading',
                file
            }));

            setItems((prev) => [...tempItems, ...prev]);
            setUploading(true);

            try {
                // sequencial (mais simples)
                for (let idx = 0; idx < files.length; idx++) {
                    const file = files[idx];
                    const tempId = tempItems[idx].id;

                    try {
                        const { data } = await galleryService.upload(clientId, file);

                        setItems((prev) =>
                            prev.map((p) =>
                                p.id === tempId
                                    ? {
                                        id: data.id,
                                        type: data.type,
                                        url: data.url,
                                        name: data.name,
                                        size: data.size,
                                        createdAt: data.createdAt,
                                        status: 'ready',
                                        file: undefined,
                                    }
                                    : p
                            )
                        );

                        // revoke do preview blob
                        const old = tempItems[idx].url;
                        if (isBlobUrl(old)) URL.revokeObjectURL(old);
                    } catch (e: any) {
                        setItems((prev) =>
                            prev.map((p) =>
                                p.id === tempId ? { ...p, status: 'error', error: e?.message || 'Erro no upload' } : p
                            )
                        );
                    }
                }
            } finally {
                setUploading(false);
            }
        },
        [clientId, galleryService]
    );

    const removeItem = useCallback(
        async (id: string) => {
            await galleryService.remove(clientId, id);

            setItems((prev) => {
                const found = prev.find((p) => p.id === id);
                if (found?.url && isBlobUrl(found.url)) URL.revokeObjectURL(found.url);
                return prev.filter((p) => p.id !== id);
            });
        },
        [clientId, galleryService]
    );

    const headerInfo = useMemo(() => {
        if (loadingList) return 'Carregando...';
        return `${total} item(ns)`;
    }, [loadingList, total]);

    return {
        items,
        loadingList,
        uploading,
        preview,
        openCamera,
        canShowEmpty,
        total,
        headerInfo,
        setPreview,
        setOpenCamera,
        fetchGallery,
        addFiles,
        removeItem,
    };
}
